/**
 * One provision/teardown run rendered as ledger steps.
 *
 * Extracted from FirstBuild so the project overview can show the SAME ledger
 * a person saw on step 3 of the create-project flow. That matters: somebody
 * who navigates away mid-provision and lands on the project page should not
 * be shown a different, vaguer account of the same run, it is the identical
 * list of steps, in the identical order, with the identical labels, because
 * the labels are the server's (`lib/serialize.ts`'s STEP_LABELS) and neither
 * screen writes its own.
 */

import type { NarrationStep } from '../components/patterns/StepLedger';
import { t } from './i18n/t';
import type { Run } from './real-api';

/**
 * `withStep` writes `failed` to D1 the instant an attempt throws, before the
 * Workflow's own retry fires (one retry, per STEP_RETRY_CONFIG). While the
 * run itself is still going, a step-level `failed` is an attempt about to be
 * retried, not a failure: show it as work in flight. If the retry also fails
 * the run goes `failed` and the cross becomes real on the next poll.
 */
export function runLedgerSteps(run: Run | null): NarrationStep[] {
  if (!run) return [];
  const runActive = run.status === 'queued' || run.status === 'running';
  return run.steps.map((step) => {
    const retrying = step.status === 'failed' && runActive;
    return {
      label: t(step.label),
      status: retrying ? 'running' : step.status,
      detail: retrying ? t('retrying') : undefined,
    };
  });
}

/**
 * cloud-api's PROVISION_STEPS, in order, with its own labels
 * (services/runs.ts, lib/serialize.ts's STEP_LABELS).
 *
 * Only ever a placeholder: it is what the ledger shows in the moment
 * between a screen deciding a project is provisioning and the first
 * `GET /runs/:id` coming back. A skeleton there would be a second loading
 * idiom inside a panel whose whole job is already "this is in progress",
 * and the step names are fixed, so showing them as pending is both honest
 * and steadier than a shimmer that is replaced a beat later.
 */
const PROVISION_STEP_LABELS = [
  'Fetching your starter template',
  'Creating your database',
  'Creating your file storage',
  'Setting up your app',
  'Publishing your web address',
  'Committing your starter code',
  'Finishing up',
] as const;

export function provisionPlaceholderSteps(): NarrationStep[] {
  return PROVISION_STEP_LABELS.map((label, index) => ({
    label: t(label),
    status: index === 0 ? 'running' : 'pending',
  }));
}

/** The step that is running right now, by its server-written label. */
export function currentStepLabel(run: Run | null): string | null {
  if (!run) return null;
  const running = run.steps.find((step) => step.status === 'running');
  if (running) return t(running.label);
  const pending = run.steps.find((step) => step.status === 'pending');
  return pending ? t(pending.label) : null;
}
